/**
 * Station-page navbar.
 *
 * Layout (left → right):
 *   - Logo
 *   - ← Stations  Vehicles      (top-level navigation)
 *   - cpid + truncated effective WS URL (click-to-copy, NIT-017)
 *   - …spacer…
 *   - Auth banner (when Authorize Accepted)
 *   - Active-tx pill
 *   - Queue pill (NIT-009)
 *   - Connection state pill (passive: coloured dot + label)
 *   - Connect / Disconnect button (active: large, primary or slate)
 *
 * Session 17 redesign:
 *   - The connection state and the action button used to share rounded
 *     pill styling, which made it hard to tell at a glance which one
 *     was a status and which one was a button. Now: the state is a
 *     compact pill with a coloured DOT, neutral background; the action
 *     is a larger, bolder button with a primary (green) or slate
 *     (dark) fill depending on direction. They no longer look alike.
 *   - The last-error indicator moved out of the top row into its own
 *     amber/rose banner row directly below the header. It carries the
 *     full message + Retry + Dismiss buttons, so a connection failure
 *     is impossible to miss and trivial to recover from.
 *   - "Vehicles" link added next to "← Stations" so users discover the
 *     vehicle library without first going back to the registry.
 */
import { ConnectionState, OfflineSessionStatus } from '@/state/types';
import { useStationStore } from '@/state/useStationStore';
import { useUIStore } from '@/state/useUIStore';
import { useOfflineQueueStore } from '@/state/useOfflineQueueStore';
import { toast, toastError } from '@/state/useToastStore';
import { buildOcppWsUrl } from '@/lib/buildOcppWsUrl';
import { CountBadge } from './CountBadge';
import { Logo } from './Logo';

/**
 * Compute the URL the WebSocket will actually dial, given the user-entered
 * base wsUrl and cpid. Falls back to the raw wsUrl if either is empty or the
 * URL is malformed — the Navbar must never crash on user input.
 */
function effectiveDialUrl(wsUrl: string, cpid: string): string {
  if (!wsUrl) return '';
  if (!cpid) return wsUrl;
  try {
    return buildOcppWsUrl(wsUrl, cpid);
  } catch {
    return wsUrl;
  }
}

interface StateBadge {
  readonly label: string;
  readonly dot: string;
  readonly dotAnim?: string;
  readonly textCls: string;
}

const BADGE_BY_STATE: Record<string, StateBadge> = {
  [ConnectionState.Disconnected]: {
    label: 'Disconnected',
    dot: 'bg-slate-500',
    textCls: 'text-slate-300',
  },
  [ConnectionState.Connecting]: {
    label: 'Connecting…',
    dot: 'bg-amber-400',
    dotAnim: 'animate-pulse',
    textCls: 'text-amber-200',
  },
  [ConnectionState.Connected]: {
    label: 'Connected',
    dot: 'bg-emerald-400',
    textCls: 'text-emerald-200',
  },
  [ConnectionState.Reconnecting]: {
    label: 'Reconnecting…',
    dot: 'bg-amber-400',
    dotAnim: 'animate-pulse',
    textCls: 'text-amber-200',
  },
  [ConnectionState.Error]: {
    label: 'Error',
    dot: 'bg-rose-500',
    textCls: 'text-rose-200',
  },
};

export function Navbar(): JSX.Element {
  const cpid = useStationStore((s) => s.cpid);
  const wsUrl = useStationStore((s) => s.wsUrl);
  const connectionState = useStationStore((s) => s.connectionState);
  const connect = useStationStore((s) => s.connect);
  const disconnect = useStationStore((s) => s.disconnect);
  const lastError = useStationStore((s) => s.lastError);
  const clearError = useStationStore((s) => s.clearError);
  const authBanner = useStationStore((s) => s.authBanner);
  const activeTxIds = useStationStore((s) =>
    Array.from(s.connectors.values())
      .map((c) => c.activeTransactionId)
      .filter((id): id is NonNullable<typeof id> => id !== null),
  );
  const queueCount = useOfflineQueueStore(
    (s) => s.entries.filter((e) => e.status === OfflineSessionStatus.Queued).length,
  );
  const setActiveTab = useUIStore((s) => s.setActiveTab);

  const badge = BADGE_BY_STATE[connectionState] ?? {
    label: 'Unknown',
    dot: 'bg-slate-500',
    textCls: 'text-slate-300',
  };
  const isConnected = connectionState === ConnectionState.Connected;
  const isBusy =
    connectionState === ConnectionState.Connecting ||
    connectionState === ConnectionState.Reconnecting;
  // Session 17 — error banner is shown whenever we have a message AND
  // the connection isn't healthy. A healthy Connected state hides it
  // automatically because BUG-002's wrapCallClearingErrorOnSuccess
  // clears `lastError` on the next successful CALLRESULT.
  const showErrorBanner = lastError !== null && !isConnected;

  return (
    <>
      <header
        className="flex flex-col gap-3 border-b border-slate-700 bg-slate-900 px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between"
        role="banner"
      >
        {/* VC-A — three distinct zones: brand | nav cluster | station identity,
            split by thin dividers so the row doesn't read as one run-on strip. */}
        <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
          {/* Mobile row 1: compact brand + nav cluster (server/name drop below) */}
          <div className="flex min-w-0 items-center gap-3">
            {/* Zone 1 — brand (compact CP mark in the app) */}
            <Logo variant="mark" size="sm" testId="navbar" href="/welcome" />
            <span className="sr-only">VirtualCharger</span>

            <span aria-hidden="true" className="hidden h-5 w-px bg-slate-700 sm:block" />

            {/* Zone 2 — navigation cluster */}
            <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                const dirty = connectionState !== ConnectionState.Disconnected;
                if (dirty && !window.confirm('Return to the station registry?')) return;
                window.location.assign('/');
              }}
              className="rounded bg-slate-800 px-2 py-1 text-xs text-slate-300 hover:bg-slate-700 hover:text-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-400"
              data-testid="nav-stations"
              aria-label="Back to station registry"
              title="Back to stations"
            >
              ← Stations
            </button>
            {/* Session 17 — Vehicles link sits next to Stations so the user can
                reach the vehicle library without going back to the registry. */}
            <a
              href="?page=vehicles"
              onClick={(e) => {
                if (connectionState !== ConnectionState.Disconnected) {
                  if (!window.confirm('Leave the station and open Vehicles?')) {
                    e.preventDefault();
                  }
                }
              }}
              className="rounded bg-slate-800 px-2 py-1 text-xs text-slate-300 hover:bg-slate-700 hover:text-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-400"
              data-testid="nav-vehicles"
              aria-label="Open vehicle library"
              title="Vehicles"
            >
              Vehicles
            </a>
            </div>
          </div>

          <span aria-hidden="true" className="hidden h-5 w-px bg-slate-700 sm:block" />

          {/* Mobile row 2: which station am I on (server + name) */}
          <div className="flex min-w-0 items-baseline gap-2">
            <span className="text-sm font-medium text-slate-200" data-testid="station-cpid">
              {cpid}
            </span>
            {/* NIT-017 — click the URL text to copy the FULL effective dial URL. */}
            {(() => {
              const url = effectiveDialUrl(wsUrl, cpid);
              if (url === '') return null;
              const handleCopy = (): void => {
                void navigator.clipboard
                  .writeText(url)
                  .then(() => toast(`Copied ${truncate(url, 60)}`))
                  .catch(() => toastError('Clipboard unavailable'));
              };
              return (
                <button
                  type="button"
                  onClick={handleCopy}
                  title={`Click to copy · ${url}`}
                  aria-label="Copy WebSocket URL"
                  className="max-w-[55vw] truncate cursor-pointer rounded text-xs text-slate-500 hover:text-slate-300 focus:outline-none focus:ring-1 focus:ring-slate-400"
                  data-testid="effective-ws-url"
                >
                  {truncate(url, 60)}
                </button>
              );
            })()}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* NIT-006: parallel badges that complement the connection state. */}
          {authBanner && authBanner.status === 'Accepted' ? (
            <span
              className="rounded bg-primary-base/70 px-2 py-1 text-xs text-primary-on"
              data-testid="nav-authorized"
              title="Last Authorize accepted"
            >
              Authorized: {authBanner.idTag}
            </span>
          ) : null}
          {activeTxIds.length > 0 ? (
            <span
              className="rounded bg-blue-800/70 px-2 py-1 text-xs text-blue-100"
              data-testid="nav-tx"
              title="Active transactions"
            >
              Tx: {activeTxIds.map((id) => `#${String(id)}`).join(' ')}
            </span>
          ) : null}
          {/* NIT-009: Queue pill — click to jump to the Offline tab. */}
          {queueCount > 0 ? (
            <button
              type="button"
              onClick={() => setActiveTab('offline')}
              className="rounded bg-amber-800/70 px-2 py-1 text-xs text-amber-50 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-300"
              data-testid="nav-queue"
              title="Open Offline Sessions tab"
            >
              Queue: {queueCount}
            </button>
          ) : (
            <CountBadge count={0} testId="nav-queue" />
          )}
          {/* Session 17 — connection state pill. Passive indicator only,
              styled distinctly from the action button: small, neutral
              slate background, ring border, and a coloured DOT that
              animates while we're (re)connecting. No hover state — it
              isn't clickable. */}
          <span
            className={`inline-flex items-center gap-1.5 rounded-full bg-slate-800/80 px-2.5 py-1 text-[11px] font-medium ring-1 ring-slate-700 ${badge.textCls}`}
            data-testid="connection-state"
            role="status"
            aria-live="polite"
          >
            <span
              aria-hidden="true"
              className={`inline-block h-2 w-2 rounded-full ${badge.dot} ${badge.dotAnim ?? ''}`}
            />
            {badge.label}
          </span>
          {/* Session 17 — action button. Visually heavier than the state
              pill: square corners with sm rounded, large padding, bold
              text, primary green when offering Connect and slate when
              offering Disconnect. The semantic difference between
              "what state am I in" and "what should I press" is now
              clear at a glance. */}
          {isConnected ? (
            <button
              type="button"
              onClick={() => {
                void disconnect();
              }}
              className="rounded-md bg-slate-700 px-3.5 py-1.5 text-sm font-semibold text-slate-100 shadow-sm hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-400"
              data-testid="disconnect"
            >
              Disconnect
            </button>
          ) : (
            <button
              type="button"
              disabled={isBusy}
              onClick={() => {
                void connect();
              }}
              className="rounded-md bg-emerald-600 px-3.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-400 disabled:cursor-not-allowed disabled:opacity-60"
              data-testid="connect"
            >
              {isBusy ? 'Connecting…' : 'Connect'}
            </button>
          )}
        </div>
      </header>
      {/* Session 17 — error banner. Shown beneath the header so a
          connection failure is impossible to miss. Carries the
          (already-masked-for-credentials) error message + Retry and
          Dismiss buttons. Dismiss just clears the in-memory error;
          the next failure will set it again. */}
      {showErrorBanner ? (
        <div
          className="flex items-start justify-between gap-3 border-b border-rose-900/60 bg-rose-950/60 px-4 py-2 text-sm text-rose-100"
          role="alert"
          aria-live="assertive"
          data-testid="nav-error-banner"
        >
          <div className="flex min-w-0 items-start gap-2">
            <span
              aria-hidden="true"
              className="mt-0.5 inline-block h-4 w-4 flex-shrink-0 rounded-full bg-rose-500 text-center text-[10px] font-bold leading-4 text-rose-50"
            >
              !
            </span>
            <span className="min-w-0 break-words" data-testid="nav-error-message">
              {lastError}
            </span>
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            <button
              type="button"
              disabled={isBusy}
              onClick={() => {
                void connect();
              }}
              className="rounded bg-rose-700 px-2.5 py-1 text-xs font-medium text-rose-50 hover:bg-rose-600 focus:outline-none focus:ring-2 focus:ring-rose-300 disabled:cursor-not-allowed disabled:opacity-60"
              data-testid="nav-error-retry"
            >
              Retry
            </button>
            <button
              type="button"
              onClick={clearError}
              className="rounded bg-rose-900/40 px-2.5 py-1 text-xs text-rose-200 hover:bg-rose-900/60 focus:outline-none focus:ring-2 focus:ring-rose-400"
              data-testid="nav-error-dismiss"
              aria-label="Dismiss error"
              title="Dismiss"
            >
              Dismiss
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}

function truncate(s: string, max: number): string {
  return s.length <= max ? s : `${s.slice(0, max - 1)}…`;
}
